<template>
  <div v-if="active" class="ot-root" role="dialog" aria-label="Guided tour">
    <div class="ot-block" @click="skip"></div>
    <div class="ot-mask" :style="maskStyle"></div>
    <div class="ot-tip" :style="tipStyle" @click.stop>
      <div class="ot-tip-title">{{ step.title }}</div>
      <div class="ot-tip-body">{{ step.body }}</div>
      <div class="ot-tip-foot">
        <span class="ot-count">{{ i + 1 }} / {{ live.length }}</span>
        <span class="ot-actions">
          <button v-if="i > 0" class="ot-btn" @click="prev">Back</button>
          <button class="ot-btn ot-skip" @click="skip">Skip</button>
          <button class="ot-btn ot-next" @click="next">{{ i === live.length - 1 ? 'Done' : 'Next →' }}</button>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
// A dependency-free guided tour: spotlights a sequence of on-screen targets
// (CSS selectors) with a tooltip + Back/Skip/Next. Steps whose target isn't on
// the page are skipped. Parent drives it via a ref: this.$refs.tour.begin().
export default {
  name: 'OnboardingTour',
  props: { steps: { type: Array, default: () => [] } },  // [{ target: 'css selector' (optional), title, body }]
  data() { return { active: false, i: 0, rect: null }; },
  computed: {
    live() { return this.steps.filter((s) => !s.target || document.querySelector(s.target)); },
    step() { return this.live[this.i] || {}; },
    maskStyle() {
      if (!this.rect) return { width: '0', height: '0', top: '50%', left: '50%', opacity: '0' };
      const p = 6;
      return { top: (this.rect.top - p) + 'px', left: (this.rect.left - p) + 'px', width: (this.rect.width + 2 * p) + 'px', height: (this.rect.height + 2 * p) + 'px', opacity: '1' };
    },
    tipStyle() {
      const W = 300;
      if (!this.rect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: W + 'px' };
      const belowSpace = window.innerHeight - this.rect.bottom;
      const top = belowSpace > 170 ? (this.rect.bottom + 14) : Math.max(12, this.rect.top - 174);
      const left = Math.max(12, Math.min(this.rect.left, window.innerWidth - W - 12));
      return { top: top + 'px', left: left + 'px', width: W + 'px' };
    },
  },
  methods: {
    begin() {
      if (!this.steps.length) { this.$emit('done'); return; }
      this.i = 0; this.active = true;
      window.addEventListener('resize', this.reposition);
      window.addEventListener('scroll', this.reposition, true);
      this.$nextTick(() => this.go());
    },
    go() {
      const el = this.step.target && document.querySelector(this.step.target);
      if (!el) { this.rect = null; return; }
      el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      setTimeout(this.reposition, 320);  // settle after the smooth scroll
    },
    reposition() {
      if (!this.active) return;
      const el = this.step.target && document.querySelector(this.step.target);
      this.rect = el ? el.getBoundingClientRect() : null;
    },
    next() { if (this.i < this.live.length - 1) { this.i++; this.go(); } else this.end(); },
    prev() { if (this.i > 0) { this.i--; this.go(); } },
    skip() { this.end(); },
    end() {
      this.active = false;
      window.removeEventListener('resize', this.reposition);
      window.removeEventListener('scroll', this.reposition, true);
      this.$emit('done');
    },
  },
  beforeUnmount() { window.removeEventListener('resize', this.reposition); window.removeEventListener('scroll', this.reposition, true); },
};
</script>

<style scoped>
.ot-root { position: fixed; inset: 0; z-index: 3000; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif; }
.ot-block { position: fixed; inset: 0; cursor: pointer; }
.ot-mask { position: fixed; border: 2px solid #ffd877; border-radius: 6px; box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.72), 0 0 14px rgba(255, 216, 119, 0.6); pointer-events: none; transition: top 0.25s ease, left 0.25s ease, width 0.25s ease, height 0.25s ease, opacity 0.2s ease; }
.ot-tip { position: fixed; box-sizing: border-box; background: #0a1f33; border: 1px solid #ffd877; border-radius: 8px; padding: 13px 15px 11px; color: #e3f1ff; box-shadow: 0 10px 34px rgba(0, 0, 0, 0.6); text-align: left; }
.ot-tip-title { font-size: 15px; font-weight: bold; color: #ffd877; margin-bottom: 5px; }
.ot-tip-body { font-size: 12.5px; line-height: 1.45; opacity: 0.92; }
.ot-tip-foot { display: flex; align-items: center; justify-content: space-between; margin-top: 12px; }
.ot-count { font-size: 11px; opacity: 0.55; font-variant-numeric: tabular-nums; }
.ot-actions { display: flex; gap: 6px; }
.ot-btn { background: rgba(12, 54, 80, 0.7); color: #cbe6ff; border: 1px solid #2f5d86; border-radius: 5px; font-size: 12px; padding: 4px 11px; cursor: pointer; }
.ot-btn:hover { background: rgba(28, 110, 164, 0.6); color: #fff; }
.ot-next { background: #c79215; border-color: #ffd877; color: #fff; font-weight: bold; }
.ot-next:hover { background: #e0a91c; }
</style>
